import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { Dropdown } from '@icons/common';

import Button from '../Button';

class Select extends React.PureComponent {
  constructor(props) {
    super(props);

    const { value: selectedValue, placeholder: allowEmpty, options } = props;
    const value = selectedValue || (!allowEmpty && options[0].value) || null;

    this.state = {
      focusedIndex: 0,
      isOpen: false,
      optionHeight: 44,
      value,
    };

    this.buttonsEls = [];
    this.el = React.createRef();

    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleRefButtons = this.handleRefButtons.bind(this);
    this.handleRefTitle = this.handleRefTitle.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.dispatchChangeEvent = this.dispatchChangeEvent.bind(this);
    this.getClosestOptionIndex = this.getClosestOptionIndex.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { value } = this.state;
    if (nextProps.value !== value) {
      this.setState(() => ({ value: nextProps.value }));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const { focusedIndex, isOpen } = this.state;
    if (
      prevState.focusedIndex !== focusedIndex ||
      (!prevState.isOpen && prevState.isOpen !== isOpen)
    ) {
      setTimeout(() => this.buttonsEls[focusedIndex].focus());
    }
  }

  handleBlur({ relatedTarget }) {
    if (!this.el.current.contains(relatedTarget)) {
      this.toggle(false);
    }
  }

  handleChange({ target }) {
    const clickedElement = this.buttonsEls.find(
      buttonEl => buttonEl && buttonEl.contains(target)
    );
    if (!clickedElement) return;

    const { value, disabled } = clickedElement.dataset;

    if (!value || disabled === 'true') {
      return;
    }

    this.toggle(false, { value }, this.dispatchChangeEvent);
  }

  handleOpen() {
    this.toggle(null, { focusedIndex: 0 }, () => {
      const { offsetHeight: optionHeight } = this.buttonsEls[0].parentElement;
      this.setState({ optionHeight });
    });
  }

  handleRefButtons(component) {
    const element = ReactDOM.findDOMNode(component);
    this.buttonsEls.push(element);
  }

  handleRefTitle(Component) {
    this.title = ReactDOM.findDOMNode(Component);
  }

  handleKeyPress(event) {
    const { key } = event;

    if (key === 'ArrowUp' || key === 'ArrowDown') {
      event.preventDefault();
      const direcion = key === 'ArrowUp' ? -1 : 1;
      const focusedIndex = this.getClosestOptionIndex(direcion);
      this.setState({ focusedIndex });
    }

    if (key === 'Escape') {
      this.toggle(false);
    }
  }

  /**
   * Возвращает индекс предыдущего или следующего option
   * @param {number} direction
   */
  getClosestOptionIndex(direction) {
    const {
      options: { length },
    } = this.props;
    const { focusedIndex } = this.state;
    const nextIndex = focusedIndex + direction;

    if (nextIndex === length) {
      return 0;
    }

    if (nextIndex < 0) {
      return length - 1;
    }

    return nextIndex;
  }

  toggle(force, state, callback) {
    this.setState(
      prevState => ({
        ...state,
        isOpen: _.isBoolean(force) ? force : !prevState.isOpen,
      }),
      () => {
        const { onToggle } = this.props;
        const { isOpen } = this.state;
        onToggle(isOpen);

        if (_.isFunction(callback)) {
          callback();
        }
      }
    );
  }

  dispatchChangeEvent() {
    const { onChange } = this.props;
    const { value } = this.state;

    const event = new CustomEvent('change', {
      bubbles: true,
      detail: {
        value,
      },
    });

    this.el.current.dispatchEvent(event);
    onChange(event);
    this.title.focus();
  }

  render() {
    const {
      disabled,
      headRenderer,
      optionRenderer,
      options,
      placeholder,
      size,
      ...props
    } = this.props;
    const { isOpen, value, optionHeight } = this.state;

    const selectedOption = _.find(options, { value });
    const optionsHeight = optionHeight * size;

    return (
      <StyledSelect {...props} onBlur={this.handleBlur} ref={this.el}>
        <StyledSelected
          functional
          disabled={disabled}
          onClick={this.handleOpen}
          ref={this.handleRefTitle}
          onKeyDown={this.handleKeyPress}
        >
          {selectedOption || !placeholder ? (
            (_.isFunction(headRenderer) && headRenderer(selectedOption)) ||
            selectedOption.title
          ) : (
            <StyledPlaceholder>{placeholder}</StyledPlaceholder>
          )}
          {!disabled && <StyledStatusIcon open={isOpen} />}
        </StyledSelected>
        <StyledOptions hidden={!isOpen} height={optionsHeight}>
          {options.map(option => (
            <StyledOption
              key={option.value}
              selected={option.value === value}
              disabled={option.disabled}
              data-selected={option.value === value}
            >
              <Button
                data-value={option.value}
                data-disabled={option.disabled}
                functional
                onClick={this.handleChange}
                ref={this.handleRefButtons}
                onKeyDown={this.handleKeyPress}
              >
                {_.isFunction(optionRenderer)
                  ? optionRenderer(option)
                  : option.title}
              </Button>
            </StyledOption>
          ))}
        </StyledOptions>
      </StyledSelect>
    );
  }
}

Select.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onToggle: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequire,
      value: PropTypes.string.isRequire,
    }).isRequired
  ).isRequired,
  headRenderer: PropTypes.func,
  optionRenderer: PropTypes.func,
  placeholder: PropTypes.string,
  size: PropTypes.number,
  value: PropTypes.string,
};

Select.defaultProps = {
  className: '',
  disabled: false,
  headRenderer: null,
  onChange: Function.prototype,
  onToggle: Function.prototype,
  optionRenderer: null,
  placeholder: null,
  size: 5,
  value: '',
};

const StyledSelect = styled.div`
  background-color: #ffffff;
  border-radius: 4px;
  color: #17181a;
  height: 36px;
  line-height: 36px;
  width: 201px;
`;

const StyledStatusIcon = styled(Dropdown)`
  width: 9px;
  height: 5px;

  path {
    fill: #000;
  }

  ${({ open }) =>
    open &&
    `
    transform: rotate(180deg);
  `}
`;

const StyledSelected = styled(Button)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: space-between;
  padding: 0 11px;
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    `
    cursor: default;
    opacity: .5;
    pointer-events: none;
  `}
`;

const StyledOptions = styled.ul`
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
  margin-top: 10px;
  position: relative;
  z-index: 2;
  overflow-y: auto;
  max-height: ${({ height }) => height}px;

  ::before {
    border-bottom: 7px solid #fff;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    content: '';
    height: 0;
    left: 50%;
    margin-left: -8px;
    position: absolute;
    top: -7px;
    width: 0;
  }

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-thumb {
    background-color: #c2c5cc;
    border-radius: 40px;
  }

  ${({ hidden }) =>
    hidden &&
    `
    display: none;
  `}
`;

const StyledOption = styled.li`
  padding: 0 3px;
  min-height: 31px;
  line-height: 31px;
  border-top: 1px solid transparent;

  > button {
    height: 100%;
    margin: 2px 0;
    padding: 0 8px;
    text-align: left;
    width: 100%;
  }

  & + & {
    border-top: 1px solid rgba(194, 197, 204, 0.2);
  }

  &:first-child {
    border-radius: 4px 4px 0 0;
  }
  &:last-child {
    border-radius: 0 0 4px 4px;
  }

  ${({ selected }) =>
    selected &&
    `
    color: #fff;
    background-color: #007aff;
  `}

  ${({ disabled }) =>
    disabled &&
    `
    opacity: .3;

    > button {
      cursor: default;
    }
  `}
`;

const StyledPlaceholder = styled.span`
  color: gray;
`;

Select.StyledSelected = StyledSelected;
Select.StyledOptions = StyledOptions;
Select.StyledOption = StyledOption;
Select.StyledStatusIcon = StyledStatusIcon;

export default Select;
